import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  documentId
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

// Firestore "in" queries have a max (commonly 30). Chunk to 25 for headroom.
const IN_CHUNK = 25;

function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function stripSO(docNo) {
  if (!docNo) return "";
  return String(docNo).replace(/^SO/i, "").trim();
}

function pillClass(kind) {
  switch (kind) {
    case "ok":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "warn":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "bad":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

function Pill({ kind, children }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        pillClass(kind),
      ].join(" ")}
    >
      {children}
    </span>
  );
}

function parseProgramDate(program, orderDateTs = null) {
  // Expect "dd/MM" or "dd/MM/yy" or "dd/MM/yyyy"
  if (!program) return null;

  const head = String(program).trim().split(/\s+/)[0];
  const m = head.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  if (!m) return null;

  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);

  let year;

  // If Program already includes a year, trust it
  if (m[3]) {
    const y = parseInt(m[3], 10);
    year = y < 100 ? 2000 + y : y;
  } else {
    // Otherwise base it on OrderDate year
    let orderDate = null;

    try {
      if (typeof orderDateTs?.toDate === "function") {
        orderDate = orderDateTs.toDate();
      } else if (orderDateTs instanceof Date) {
        orderDate = orderDateTs;
      } else if (orderDateTs) {
        const parsed = new Date(orderDateTs);
        if (!Number.isNaN(parsed.getTime())) orderDate = parsed;
      }
    } catch {
      orderDate = null;
    }

    if (!orderDate) return null;

    year = orderDate.getFullYear();

    // Handle year rollover:
    // If order date is late in year and program month/day looks early next year,
    // assume program is in the next year.
    //
    // Example:
    // OrderDate = 24/12/2025
    // Program   = 01/01
    // => 01/01/2026
    if (
      orderDate.getMonth() >= 9 &&   // Oct/Nov/Dec
      month <= 3                     // Jan/Feb/Mar
    ) {
      year += 1;
    }
  }

  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;

  return d;
}

function diffDays(from, to) {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.floor((a - b) / (1000 * 60 * 60 * 24));
}

function formatTs(ts) {
  try {
    const d = ts?.toDate?.();
    if (d instanceof Date) return d.toLocaleString();
  } catch {}
  return "-";
}

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// “on hand” / “still stock” definition for warehouse rows
function isWarehouseStock(doc) {
  // Your own definition can evolve.
  // Here: Stock=true and Dispatch=false means it's physically there (not dispatched)
  return doc?.Stock === true && doc?.Dispatch === false;
}

export default function StockSalesWarehouseLinker() {
  const [error, setError] = useState("");

  // NAV live
  const [stockRows, setStockRows] = useState([]);
  const [loadingStock, setLoadingStock] = useState(true);

  // Warehouse stock
  const [warehouseRows, setWarehouseRows] = useState([]);
  const [loadingWarehouse, setLoadingWarehouse] = useState(true);

  // Join state
  const [loadingJoin, setLoadingJoin] = useState(false);
  const [cylInfoByProductCode, setCylInfoByProductCode] = useState(new Map()); // productCode -> cyl_info {docId,...}
  const [productCodeByProductId, setProductCodeByProductId] = useState(new Map()); // ProductId(docId) -> productCode
  const [warehouseByKey, setWarehouseByKey] = useState(new Map()); // `${productId}__${serial}` -> warehouse doc

  // UI
  const [search, setSearch] = useState("");
  const [agedOnly, setAgedOnly] = useState(false);

  const [statusFilter, setStatusFilter] = useState("all");
  // statuses:
  // nav_missing_young, nav_missing_aged, nav_booked_in, wh_not_open_nav

  // 1) Subscribe to NAV stock_sales_live
  useEffect(() => {
    setError("");
    setLoadingStock(true);

    const q = query(
      collection(db, "stock_sales_live"),
      orderBy("LastNavSyncUtc", "desc"),
      limit(2000)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setStockRows(rows);
        setLoadingStock(false);
      },
      (err) => {
        setError(err?.message || String(err));
        setLoadingStock(false);
      }
    );

    return () => unsub();
  }, []);

  // 2) Subscribe to warehouse “stock” view
  // NOTE: If this needs an index, drop the where() and filter client-side.
  useEffect(() => {
    setError("");
    setLoadingWarehouse(true);

    // Keep it simple to avoid index pain:
    // Fetch a big chunk and filter client-side by Stock/Dispatch.
    const q = query(collection(db, "cylinders_warehouse"), limit(5000));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setWarehouseRows(rows);
        setLoadingWarehouse(false);
      },
      (err) => {
        setError(err?.message || String(err));
        setLoadingWarehouse(false);
      }
    );

    return () => unsub();
  }, []);

  // 3) Build join maps:
  // - productCode -> cyl_info docId (for NAV rows)
  // - productId(docId) -> productCode (for warehouse-only rows)
  // - warehouseByKey for exact matches
  useEffect(() => {
    let cancelled = false;

    async function runJoin() {
      setError("");
      setLoadingJoin(true);

      try {
        const now = new Date();

        // NAV product codes (as-is; cyl_info productCode is case sensitive)
        const navProductCodes = Array.from(
          new Set(
            (stockRows || [])
              .map((r) => (r?.SerialNumber ? String(r.SerialNumber).trim() : ""))
              .filter(Boolean)
          )
        );

        // Warehouse ProductIds
        const whStock = (warehouseRows || []).filter(isWarehouseStock);
        const warehouseProductIds = Array.from(
          new Set(whStock.map((w) => (w?.ProductId ? String(w.ProductId).trim() : "")).filter(Boolean))
        );

        // If nothing, clear maps
        if (navProductCodes.length === 0 && warehouseProductIds.length === 0) {
          if (!cancelled) {
            setCylInfoByProductCode(new Map());
            setProductCodeByProductId(new Map());
            setWarehouseByKey(new Map());
            setLoadingJoin(false);
          }
          return;
        }

        // A) Fetch cyl_info for NAV codes (by productCode)
        const cylByCode = new Map();
        for (const chunk of chunkArray(navProductCodes, IN_CHUNK)) {
          const qi = query(collection(db, "cyl_info"), where("productCode", "in", chunk));
          const snap = await getDocs(qi);
          snap.forEach((docSnap) => {
            const data = docSnap.data() || {};
            const pc = data.productCode ? String(data.productCode).trim() : "";
            if (pc) cylByCode.set(pc, { docId: docSnap.id, ...data });
          });
        }

        // B) Fetch cyl_info for warehouse ProductIds (by doc id)
        // so we can render warehouse-only rows with productCode
        const codeByPid = new Map();
        if (warehouseProductIds.length > 0) {
          for (const chunk of chunkArray(warehouseProductIds, IN_CHUNK)) {
            const qi2 = query(
            collection(db, "cyl_info"),
            where(documentId(), "in", chunk) // ✅ chunk is the array of doc ids
            );
            const snap2 = await getDocs(qi2);
            snap2.forEach((docSnap) => {
              const data = docSnap.data() || {};
              const pc = data.productCode ? String(data.productCode).trim() : "";
              if (pc) codeByPid.set(String(docSnap.id), pc);

              // Also backfill cylByCode for consistency if not already present
              if (pc && !cylByCode.has(pc)) cylByCode.set(pc, { docId: docSnap.id, ...data });
            });
          }
        }

        // C) Build warehouse exact-match key map from warehouse stock docs
        const whByKey = new Map();
        for (const w of whStock) {
          const pid = w?.ProductId ? String(w.ProductId).trim() : "";
          const ser = w?.SerialNumber ? String(w.SerialNumber).trim() : "";
          if (!pid || !ser) continue;
          whByKey.set(`${pid}__${ser}`, w);
        }

        if (!cancelled) {
          setCylInfoByProductCode(cylByCode);
          setProductCodeByProductId(codeByPid);
          setWarehouseByKey(whByKey);
          setLoadingJoin(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setLoadingJoin(false);
        }
      }
    }

    runJoin();
    return () => {
      cancelled = true;
    };
  }, [stockRows, warehouseRows]);

  // 4) Build the “aged stock report” rows
  const reportRows = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    const now = new Date();

    // Warehouse stock docs (filtered)
    const whStock = (warehouseRows || []).filter(isWarehouseStock);

    // Track which warehouse keys are “open on NAV” (so we can find warehouse-only anomalies)
    const navKeySet = new Set();

    const navReport = [];

    for (const r of stockRows || []) {
      const productCode = r?.SerialNumber ? String(r.SerialNumber).trim() : "";
      if (!productCode) continue;

      // ✅ IGNORE if no cyl_info match
      const cyl = cylInfoByProductCode.get(productCode);
      if (!cyl?.docId) continue;

      const productId = String(cyl.docId);
      const soDocNo = r?.DocumentNo ? String(r.DocumentNo).trim() : "";
      const navSerial = stripSO(soDocNo);
      if (!navSerial) continue;

      const key = `${productId}__${navSerial}`;
      navKeySet.add(key);

      const warehouseExact = warehouseByKey.get(key) || null;

      const program = r?.Program ? String(r.Program).trim() : "";
      const programDate = parseProgramDate(program, r?.OrderDate);
      const ageDays = programDate ? diffDays(now, programDate) : null;

      const isBookedIn = !!warehouseExact;
      const isAged = !isBookedIn && ageDays !== null && ageDays >= 10;

      let status;
      if (isBookedIn) {
        status = { id: "nav_booked_in", kind: "ok", label: "Open on NAV + booked in" };
      } else if (isAged) {
        status = { id: "nav_missing_aged", kind: "bad", label: `Open on NAV + NOT booked in (AGED ${ageDays}d)` };
      } else {
        status = { id: "nav_missing_young", kind: "warn", label: "Open on NAV + not booked in (in production?)" };
      }

      const lastNavSync = formatTs(r?.LastNavSyncUtc);

      const hay = [
        productCode,
        soDocNo,
        navSerial,
        productId,
        program,
        r?.ShipToName || "",
        warehouseExact?.LocationId || "",
        warehouseExact?.SerialNumber || "",
      ]
        .join(" ")
        .toLowerCase();

      const passesSearch = !q || hay.includes(q);
      const passesAged = !agedOnly || status.id === "nav_missing_aged";
      const passesStatus = statusFilter === "all" || status.id === statusFilter;

      if (passesSearch && passesAged && passesStatus) {
        navReport.push({
          rowType: "nav",
          status,
          productCode,
          productId,
          soDocNo,
          serial: navSerial,
          program,
          programDate,
          orderDate: r?.OrderDate || null,
          ageDays,
          lastNavSync,
          warehouse: warehouseExact,
        });
      }
    }

    // Warehouse-only anomalies:
    // “This cylinder is NOT open in NAV but is showing as stock in the warehouse (bad)”
    const whReport = [];
    for (const w of whStock) {
      const pid = w?.ProductId ? String(w.ProductId).trim() : "";
      const ser = w?.SerialNumber ? String(w.SerialNumber).trim() : "";
      if (!pid || !ser) continue;

      // ✅ Need productCode; if no cyl_info match -> IGNORE
      const productCode = productCodeByProductId.get(pid);
      if (!productCode) continue;

      const key = `${pid}__${ser}`;
      const isOpenOnNav = navKeySet.has(key);
      if (isOpenOnNav) continue; // those are covered by nav_report (booked-in ones)

      const status = {
        id: "wh_not_open_nav",
        kind: "bad",
        label: "Warehouse stock but NOT open on NAV (check booking-out)",
      };

      const hay = [
        productCode,
        pid,
        ser,
        w?.LocationId || "",
        w?.Special || "",
      ]
        .join(" ")
        .toLowerCase();

      const passesSearch = !q || hay.includes(q);
      const passesAged = !agedOnly; // agedOnly is NAV-specific
      const passesStatus = statusFilter === "all" || status.id === statusFilter;

      if (passesSearch && passesAged && passesStatus) {
        whReport.push({
          rowType: "warehouse",
          status,
          productCode,
          productId: pid,
          soDocNo: "-", // not applicable
          serial: ser,
          program: "-",
          programDate: null,
          orderDate: null,
          ageDays: null,
          lastNavSync: "-",
          warehouse: w,
        });
      }
    }

    // Combine and sort: worst first
    const combined = [...navReport, ...whReport];

    const severity = (sid) => {
      if (sid === "nav_missing_aged") return 3;
      if (sid === "wh_not_open_nav") return 2;
      if (sid === "nav_missing_young") return 1;
      if (sid === "nav_booked_in") return 0;
      return 0;
    };

    combined.sort((a, b) => {
      const sa = severity(a.status.id);
      const sb = severity(b.status.id);
      if (sb !== sa) return sb - sa;

      // then by productCode
      if (a.productCode !== b.productCode) return a.productCode.localeCompare(b.productCode);

      // then by SO/serial
      return String(a.serial || "").localeCompare(String(b.serial || ""));
    });

    return combined;
  }, [
    stockRows,
    warehouseRows,
    cylInfoByProductCode,
    productCodeByProductId,
    warehouseByKey,
    search,
    agedOnly,
    statusFilter,
  ]);

  const stats = useMemo(() => {
    const total = reportRows.length;
    const aged = reportRows.filter((r) => r.status.id === "nav_missing_aged").length;
    const prod = reportRows.filter((r) => r.status.id === "nav_missing_young").length;
    const booked = reportRows.filter((r) => r.status.id === "nav_booked_in").length;
    const whBad = reportRows.filter((r) => r.status.id === "wh_not_open_nav").length;
    return { total, aged, prod, booked, whBad };
  }, [reportRows]);

  function exportCsv() {
    const header = [
      "Status",
      "StatusId",
      "RowType",
      "ProductCode",
      "ProductId",
      "SO_DocNo",
      "Serial",
      "Program",
      "ProgramDate",
      "OrderDate",
      "AgeDays",
      "WarehouseSerial",
      "WarehouseLocation",
      "WarehouseAllocated",
      "WarehouseStock",
      "WarehouseDispatch",
      "LastNavSync",
    ];

    const rows = reportRows.map((r) => {
      const w = r.warehouse || {};
      return [
        r.status.label,
        r.status.id,
        r.rowType,
        r.productCode || "",
        r.productId || "",
        r.soDocNo || "",
        r.serial || "",
        r.program || "",
        r.programDate ? r.programDate.toLocaleDateString("en-GB") : "",
        r.orderDate ? formatTs(r.orderDate) : "",
        r.ageDays == null ? "" : String(r.ageDays),
        w?.SerialNumber != null ? String(w.SerialNumber) : "",
        w?.LocationId != null ? String(w.LocationId) : "",
        w?.Allocated === true ? "true" : w?.Allocated === false ? "false" : "",
        w?.Stock === true ? "true" : w?.Stock === false ? "false" : "",
        w?.Dispatch === true ? "true" : w?.Dispatch === false ? "false" : "",
        r.lastNavSync || "",
      ];
    });

    downloadCsv(`aged_stock_report_${new Date().toISOString().slice(0, 10)}.csv`, [header, ...rows]);
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">Aged stock report</h1>
          <p className="mt-1 text-sm text-slate-600">
            Line-by-line exceptions between{" "}
            <span className="font-mono">stock_sales_live</span> and{" "}
            <span className="font-mono">cylinders_warehouse</span>.
            <span className="ml-2 text-slate-500">
              (Ignores anything without a <span className="font-mono">cyl_info</span> match.)
            </span>
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Pill kind={loadingStock ? "warn" : "ok"}>
              {loadingStock ? "Loading NAV…" : `NAV rows: ${stockRows.length}`}
            </Pill>
            <Pill kind={loadingWarehouse ? "warn" : "ok"}>
              {loadingWarehouse ? "Loading warehouse…" : `Warehouse rows: ${warehouseRows.length}`}
            </Pill>
            <Pill kind={loadingJoin ? "warn" : "ok"}>
              {loadingJoin ? "Linking…" : "Linked"}
            </Pill>

            <Pill kind={stats.aged > 0 ? "bad" : "ok"}>Aged (10d+): {stats.aged}</Pill>
            <Pill kind={stats.prod > 0 ? "warn" : "ok"}>Not booked in: {stats.prod}</Pill>
            <Pill kind="ok">Booked in: {stats.booked}</Pill>
            <Pill kind={stats.whBad > 0 ? "bad" : "ok"}>Warehouse-only bad: {stats.whBad}</Pill>
          </div>
        </div>

        <div className="w-full max-w-xl">
          <label className="text-xs font-medium text-slate-600">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="product code, SO, serial, location…"
            className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
          />

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              onClick={() => setAgedOnly((v) => !v)}
              className={[
                "rounded-lg border px-3 py-2 text-sm font-medium",
                agedOnly
                  ? "border-rose-200 bg-rose-50 text-rose-700"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              {agedOnly ? "Showing: Aged only" : "Filter: Aged only"}
            </button>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
              title="Status filter"
            >
              <option value="all">All statuses</option>
              <option value="nav_missing_young">Open NAV + not booked in</option>
              <option value="nav_missing_aged">Open NAV + aged 10d+</option>
              <option value="nav_booked_in">Open NAV + booked in</option>
              <option value="wh_not_open_nav">Warehouse stock but not open on NAV</option>
            </select>

            <button
              onClick={exportCsv}
              className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Export CSV
            </button>
          </div>

          {error ? (
            <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          ) : null}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Product code</th>
                <th className="px-4 py-3 font-medium">SO DocNo</th>
                <th className="px-4 py-3 font-medium">Serial</th>
                <th className="px-4 py-3 font-medium">ProductId</th>
                <th className="px-4 py-3 font-medium">Warehouse serial</th>
                <th className="px-4 py-3 font-medium">Location</th>
                <th className="px-4 py-3 font-medium">Allocated</th>
                <th className="px-4 py-3 font-medium">Program</th>
                <th className="px-4 py-3 font-medium">Program date</th>
                <th className="px-4 py-3 font-medium">Age</th>
                <th className="px-4 py-3 font-medium">Last NAV sync</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {reportRows.map((row, idx) => {
                const w = row.warehouse || null;

                return (
                  <tr key={`${row.rowType}__${row.productId}__${row.serial}__${idx}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <Pill kind={row.status.kind}>{row.status.label}</Pill>
                    </td>

                    <td className="px-4 py-3 font-mono">{row.productCode || "-"}</td>
                    <td className="px-4 py-3 font-mono">{row.soDocNo || "-"}</td>
                    <td className="px-4 py-3 font-mono">{row.serial || "-"}</td>
                    <td className="px-4 py-3 font-mono text-slate-600">{row.productId || "-"}</td>

                    <td className="px-4 py-3 font-mono">{w?.SerialNumber || "-"}</td>
                    <td className="px-4 py-3">{w?.LocationId ? <span className="font-mono">{w.LocationId}</span> : "-"}</td>

                    <td className="px-4 py-3">
                      {w ? (
                        w.Allocated === true ? (
                          <Pill kind="ok">true</Pill>
                        ) : w.Allocated === false ? (
                          <Pill kind="warn">false</Pill>
                        ) : (
                          "-"
                        )
                      ) : (
                        "-"
                      )}
                    </td>

                    <td className="px-4 py-3">{row.program || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.programDate ? row.programDate.toLocaleDateString("en-GB") : "-"}
                    </td>
                    <td className="px-4 py-3">
                      {row.ageDays == null ? (
                        "-"
                      ) : row.status.id === "nav_missing_aged" ? (
                        <Pill kind="bad">{row.ageDays}d</Pill>
                      ) : (
                        <Pill kind="warn">{row.ageDays}d</Pill>
                      )}
                    </td>

                    <td className="px-4 py-3 text-slate-600">{row.lastNavSync || "-"}</td>
                  </tr>
                );
              })}

              {reportRows.length === 0 && !(loadingStock || loadingWarehouse || loadingJoin) ? (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-slate-500">
                    No rows (check filters/search).
                  </td>
                </tr>
              ) : null}

              {loadingStock || loadingWarehouse || loadingJoin ? (
                <tr>
                  <td colSpan={12} className="px-4 py-10 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 text-xs text-slate-500">
        Matching logic:
        <span className="ml-2 font-mono">NAV.SerialNumber (product code)</span> →
        <span className="ml-1 font-mono">cyl_info.productCode</span> (docId becomes ProductId) AND
        <span className="ml-2 font-mono">stripSO(NAV.DocumentNo)</span> →
        <span className="ml-1 font-mono">cylinders_warehouse.SerialNumber</span>.
        <span className="ml-2">
          Any row without a <span className="font-mono">cyl_info</span> match is ignored.
        </span>
      </div>
    </div>
  );
}